import useRest from '../../hooks/useRest'
import React, { useEffect, useState } from 'react'
import { IFilter, IGenericObject, TableKey, TableWithFields } from '@types'
import { ColumnData } from '@ss/meta-core-components/lib/components/Table'
import { Card, Spinner, Table } from '@ss/meta-core-components'
import styled from '@emotion/styled'
import { generateQuery } from '@utils'
import { SelectColumnIcon } from '../../assets/icons/selectColumnIcon'

interface Props {
  params: TableWithFields
  setSelectedColumns?: React.Dispatch<React.SetStateAction<TableKey[]>>
  selectedColumns: TableKey[]
  optionalFilter?: IGenericObject<IFilter | null>
  columnWidth?: number[]
}

/**
 * Componente per la gestione delle richieste per la tabella principale delle pagine
 * @param props.params {TableWithFields} filtri per la richiesta
 * @param props.selectedColumns {React.Dispatch<React.SetStateAction<string[]>>}
 * @param props.setSelectedColumns {string[]}
 * @param props.optionalFilter {IGenericObject<IFilter | null> | undefined} filtri di ricerca addizionali
 * @param props.columnWidth {number[] | undefined} width di ogni colonna
 * @component
 */
export const MainTable: React.FC<Props> = (props) => {
  const {
    params,
    selectedColumns,
    setSelectedColumns,
    columnWidth,
    optionalFilter,
  } = props

  const { post: getTableData } = useRest('/main_table')
  const [column, setColumn] = useState<ColumnData<any>[]>([])
  const [data, setData] = useState<IGenericObject[]>([])

  const generateColumns = () => {
    const newColumn: ColumnData<any>[] = []
    params.keys.forEach((key, index) => {
      const column: ColumnData<any> = {
        key: key.value,
        label: key.value,
        align: 'center',
        template: (data) => (
          <CellText
            length={`${
              columnWidth && columnWidth[index]
                ? columnWidth[index] + 'px'
                : '100%'
            }`}
          >
            {data[key.value]}
          </CellText>
        ),
        width:
          columnWidth && columnWidth[index]
            ? columnWidth[index] + 'px'
            : `${Math.floor(100 / params.keys.length)}%`,
      }
      if (setSelectedColumns) {
        column.onClick = () => {
          setSelectedColumns((old) => {
            if (old.find((col) => col.value === key.value)) {
              return old.filter((col) => col.value !== key.value)
            }
            return [...old, key]
          })
        }
        column.actionIcon = <SelectColumnIcon />
        column.selected = !!selectedColumns.find(
          (sel) => sel.value === key.value
        )
      }
      newColumn.push(column)
    })
    setColumn([...newColumn])
  }

  //effect per l'invio della richiesta rest per i dati della tabella
  useEffect(() => {
    if (params.keys.length > 0 && params.index) {
      let body: IGenericObject = {
        index: params.index,
        fields: params.keys.map((k) => k.value),
        query_data: generateQuery({
          params: params.filters,
          additionalFilters: optionalFilter,
        }),
        range: [],
        term: [],
      }

      if (params.sort_field && params.sort_type) {
        body.sort_field = params.sort_field
        body.sort_type = params.sort_type
      }
      getTableData(body).then((res) => {
        setData(res.data)
      })
    }
  }, [params])

  //effect per la generazione delle colonne della tabella
  useEffect(() => {
    generateColumns()
  }, [selectedColumns, params])
  //effect per l'eliminazione automatica di una colonna selezionata se l'attributo legato a essa viene rimosso dai parametri di ricerca
  useEffect(() => {
    if (setSelectedColumns) {
      const newSelected: TableKey[] = []
      params.keys.forEach((key) => {
        if (selectedColumns.find((sel) => sel.value === key.value))
          newSelected.push(key)
      })
      setSelectedColumns(newSelected)
    }
  }, [params])

  return (
    <Card width={'100%'} height={'337px'} dataCy={'main-table-container'}>
      {column.length > 0 ? (
        <Table tableColumn={column} data={data} dataCy={'main-table'} />
      ) : (
        <SpinnerContainer data-cy={'no-column-data'}>
          <Spinner />
          Loading
        </SpinnerContainer>
      )}
    </Card>
  )
}

const SpinnerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`
const CellText = styled.div<{ length: string }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: ${({ length }) => length};
`
